import { createLogger } from '@/lib/logger'

import { isTauri } from './tauri'

const logger = createLogger('lib/utils/native-progress')
const transfers = new Map<symbol, number | undefined>()
let updates = Promise.resolve()

function publishProgress() {
  // Serialize IPC so a slow "started" update cannot outlive "finished".
  updates = updates.then(async () => {
    try {
      const { getCurrentWindow, ProgressBarStatus } =
        await import('@tauri-apps/api/window')
      const values = [...transfers.values()]
      await getCurrentWindow().setProgressBar({
        status: !values.length
          ? ProgressBarStatus.None
          : values.some(value => value === undefined)
            ? ProgressBarStatus.Indeterminate
            : ProgressBarStatus.Normal,
        progress:
          values.length && values.every(value => value !== undefined)
            ? Math.round(
                values.reduce<number>((sum, value) => sum + (value ?? 0), 0) /
                  values.length,
              )
            : undefined,
      })
    } catch (error) {
      // OS feedback must never interrupt a successful upload or save.
      logger.debug('Native progress unavailable:', error)
    }
  })
}

/** OS progress is indeterminate until the operation has a meaningful measure. */
export async function withNativeProgress<T>(
  operation: (report: (percent: number) => void) => Promise<T>,
): Promise<T> {
  if (!isTauri()) return operation(() => {})
  const id = Symbol('transfer')
  transfers.set(id, undefined)
  publishProgress()
  try {
    return await operation(percent => {
      if (!Number.isFinite(percent)) return
      transfers.set(id, Math.max(0, Math.min(100, percent)))
      publishProgress()
    })
  } finally {
    transfers.delete(id)
    publishProgress()
  }
}
